import React from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronRight, Star } from 'lucide-react';
// SPEC-535: sizes come from the one nav scale (tier 3 = sub-tab rows and their labels).
import { NAV_SCALE } from '@/config/nav-scale';

export interface SectionNavItem {
  key: string;
  label: string;
  /** Diagnostic/expert item — rendered behind the group's collapsed "Advanced" toggle. */
  advanced?: boolean;
  /** Honest data-source blurb, shown on hover. */
  tooltip?: string;
}

export interface SectionNavGroup {
  label: string;
  items: SectionNavItem[];
}

interface SectionNavProps {
  groups: SectionNavGroup[];
  /** Page path the ?tab= links attach to, e.g. /demand-planning */
  basePath: string;
  /** Active ?tab= key ('' when the URL carries none → first visible item is active). */
  activeKey: string;
  /** Tab keys allowed for this workspace/user; undefined = all visible (SPEC-057 filtering). */
  visibleKeys?: string[];
  onNavigate?: () => void;
  /* SPEC-456 — the sidebar's Favourites pin, on sub-view rows. BOTH props are needed for
   * the star to render, so the horizontal SectionNavBar and any other consumer stays
   * exactly as it was: a star there would pin into a group that strip cannot show. */
  /** Tab keys of THIS page that are currently pinned. */
  pinnedTabKeys?: string[];
  /** Pin or unpin one sub-view. The caller owns the stored entry format. */
  onTogglePin?: (tabKey: string) => void;
  /** Page name, so the pin's accessible label names the page as well as the sub-view. */
  pageLabel?: string;
  /** SPEC-535: DOM id of the strip, for the page row's chevron (`aria-controls`). */
  id?: string;
  /** SPEC-535: false when the strip is opened by the chevron on a page you are NOT on —
   *  then no tab is marked as the current one. Defaults to true (the old behaviour). */
  pageActive?: boolean;
}

const LS_KEY = 'suryaiq_nav_advanced';

/* SPEC-459 — the indentation grammar, in ONE place.
 *
 * Param's reading of the live nav: "Scenarios should be nested underneath [Advanced], to
 * the side a little bit. If it's nested, it should be actually nested." It was not. Every
 * row in this strip sat at `pl-4` — the group header, its items, the Advanced toggle and
 * the items the toggle reveals — so three levels of structure rendered as one flat column
 * and the only thing distinguishing a group header was its type size.
 *
 * Depth is now the left pad, one 8px step per level, and NOTHING else encodes it:
 *   level 0  the group header, and the items of an UNLABELLED group
 *   level 1  the items of a labelled group, and that group's Advanced toggle
 *   level 2  the items the Advanced toggle reveals
 *
 * Level 0 for an unlabelled group's items is deliberate, not an oversight. The registry
 * gives a flat page one group with an empty label (Intelligence, Products) and this strip
 * renders no header for it; indenting those items would nest them under nothing, which is
 * the same lie in the other direction.
 *
 * 8px is "a little bit" on purpose: the labels are 12px and one of them is "Planning,
 * Scheduling & Replenishment" in a 240px sidebar, so a 12px step would buy clarity at the
 * cost of wrapping. The left border rail gives the eye its reference.
 */
const DEPTH_PAD = ['pl-4', 'pl-6', 'pl-8'] as const;

/* CLAMPED, not indexed raw (review finding 9). `strict` is on but
 * `noUncheckedIndexedAccess` is not, so `DEPTH_PAD[3]` type-checks as a string and renders
 * the class `"undefined"`, which Tailwind drops — a fourth level would silently UN-nest
 * instead of failing the build. Clamping degrades to the deepest pad we have. */
const padAt = (depth: number): string => DEPTH_PAD[Math.min(depth, DEPTH_PAD.length - 1)];

function loadAdvancedState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Grouped section sub-nav (SPEC-112): one nav idiom shared by Demand Planning and
 * Governance. Groups mirror the planner's loop; diagnostic items stay reachable
 * behind a per-group "Advanced" toggle (curate, don't cull).
 */
export function SectionNav({
  groups,
  basePath,
  activeKey,
  visibleKeys,
  onNavigate,
  pinnedTabKeys,
  onTogglePin,
  pageLabel,
  id,
  pageActive = true,
}: SectionNavProps) {
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: visibleKeys ? g.items.filter((i) => visibleKeys.includes(i.key)) : g.items,
    }))
    .filter((g) => g.items.length > 0);

  const firstVisibleKey = visibleGroups[0]?.items[0]?.key;

  const [advancedOpen, setAdvancedOpen] = React.useState<Record<string, boolean>>(() => loadAdvancedState());

  const toggleAdvanced = (groupLabel: string) => {
    setAdvancedOpen((prev) => {
      const next = { ...prev, [`${basePath}:${groupLabel}`]: !prev[`${basePath}:${groupLabel}`] };
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(next));
      } catch { /* layout pref only */ }
      return next;
    });
  };

  const pinnable = Boolean(onTogglePin && pinnedTabKeys);

  /* One stable id prefix per mounted strip, for the group→label association below. React
   * owns it (`useId`) rather than a slug of basePath + label: two strips can be mounted at
   * once (a pinned page's row in Favourites and the same page's verb-group row), and a
   * duplicated `id` would point both groups' `aria-labelledby` at one element. */
  const idBase = React.useId();

  /* SPEC-459 — CURRIED by depth, so the returned renderer stays SINGLE-ARGUMENT: the
   * callers below are `.map(renderLinkAt(pad))`, and `.map` would feed the array INDEX in
   * as a second parameter to anything that grew one. Take the pad here, never there. */
  const renderLinkAt = (pad: string) => (item: SectionNavItem) => {
    const isActive = pageActive && (activeKey === item.key || (!activeKey && item.key === firstVisibleKey));
    const link = (
      <NavLink
        to={`${basePath}?tab=${item.key}`}
        onClick={onNavigate}
        title={item.tooltip}
        // SPEC-535 review finding 2: 'false', never undefined. NavLink matches the PATHNAME
        // only, so every tab of the page on screen is "active" to it, and an undefined prop
        // falls back to NavLink's default 'page' — every tab announced as the current one.
        aria-current={isActive ? 'page' : 'false'}
        className={`block ${pinnable ? 'flex-1 min-w-0 ' : ''}${pad} pr-3 ${NAV_SCALE.sub.row} ${NAV_SCALE.sub.text} rounded-r-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${
          isActive
            ? `text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 -ml-[1px] ${NAV_SCALE.sub.weightActive}`
            : `${NAV_SCALE.sub.weight} text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]`
        }`}
      >
        {item.label}
      </NavLink>
    );
    if (!pinnable) return <React.Fragment key={item.key}>{link}</React.Fragment>;

    /* SPEC-456 — the pin control, same contract as the page row's in Sidebar.tsx: a flex
       PAIR rather than a star inside the anchor (nested interactives break a11y and
       swallow the navigation click), always in the tab order, revealed by hover, by
       focus-visible and unconditionally on touch, with a STABLE label plus aria-pressed
       so a screen reader does not announce the toggle twice. */
    const isPinned = pinnedTabKeys!.includes(item.key);
    const name = pageLabel ? `${pageLabel} · ${item.label}` : item.label;
    return (
      <div key={item.key} className="flex items-center gap-1 group/sub">
        {link}
        <button
          type="button"
          onClick={() => onTogglePin!(item.key)}
          aria-pressed={isPinned}
          aria-label={`Pin ${name} to Favourites`}
          // SPEC-456 review finding 4: p-1.5 + w-3.5 = a 28px target, clearing the WCAG
          // 2.5.8 24px floor and matching the page row's star exactly. The strip can show
          // 18 of these on Demand Planning, each revealed on touch beside a navigation
          // link, so an under-size target mis-navigates on a tablet.
          title={isPinned ? 'Unpin from Favourites' : 'Pin to Favourites'}
          className={`flex-shrink-0 p-1.5 rounded transition-colors hover:bg-[var(--sidebar-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 ${
            isPinned
              ? 'text-amber-400'
              : 'text-[var(--text-tertiary)] opacity-0 group-hover/sub:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 hover:text-amber-400'
          }`}
        >
          <Star className={NAV_SCALE.page.icon} fill={isPinned ? 'currentColor' : 'none'} />
        </button>
      </div>
    );
  };

  return (
    // SPEC-535: the thin guide line sits under the page name (one step in); the rows pad
    // from it, so a sub-tab reads two steps in from its group heading.
    <div id={id} className={`${NAV_SCALE.sub.guide} mt-0.5 space-y-1.5`}>
      {visibleGroups.map((group, gi) => {
        const primary = group.items.filter((i) => !i.advanced);
        const advanced = group.items.filter((i) => i.advanced);
        const stateKey = `${basePath}:${group.label}`;
        // A deep link into an advanced item must never land on a hidden tab.
        const isOpen = advancedOpen[stateKey] || advanced.some((i) => i.key === activeKey);
        // SPEC-459 — an unlabelled group has no header to nest under, so its items stay at
        // level 0; a labelled group's items sit one step in, and Advanced one step further.
        const itemDepth = group.label ? 1 : 0;
        const labelId = `${idBase}-g${gi}`;
        return (
          /* SPEC-459 review finding 8 — the nesting a sighted user now reads needs a
             programmatic equivalent, or it exists for one audience only. `role="group"`
             plus `aria-labelledby` is that equivalent: a screen reader announces the
             group's name on entering it, which is what the indent and the heading colour
             say visually. An UNLABELLED group gets neither — there is no name to point
             at, and an unnamed group is noise. */
          <div
            key={group.label}
            role={group.label ? 'group' : undefined}
            aria-labelledby={group.label ? labelId : undefined}
          >
            {/* Flat pages (SPEC-139) carry a single empty-label group — no header.
                SPEC-459: the heading colour is `--nav-heading`, the SAME token the
                sidebar's group headers use, because Param asked for one treatment for
                every non-clickable group label. This one is a plain div, not a button —
                it is a label and must not grow a fake affordance, so it carries no
                chevron either (the chevron is this nav's "I am a control" mark). */}
            {group.label && (
              <div
                id={labelId}
                className={`${padAt(0)} pr-3 pt-1.5 pb-0.5 ${NAV_SCALE.subLabel.text} text-[var(--nav-heading)]`}
              >
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {primary.map(renderLinkAt(padAt(itemDepth)))}
              {advanced.length > 0 && (
                <>
                  {/* The toggle sits WITH the items it belongs beside; the items it
                      reveals sit one step inside it (Param: "Scenarios... nested
                      underneath [Advanced], to the side a little bit").
                      Review finding 3: "Advanced" is one of the four headings Param named
                      verbatim ("'Today', 'Plan', 'Advanced', 'Buy'"), so it carries
                      `--nav-heading` like the other three — and the hover lift, because it
                      IS a control. It keeps its own form (chevron + count, not uppercase;
                      SPEC-535: the sub-tab tier's 12px, was 11px): the chevron is what says
                      "this one does something". */}
                  <button
                    onClick={() => toggleAdvanced(group.label)}
                    aria-expanded={isOpen}
                    className={`w-full flex items-center gap-1 ${padAt(itemDepth)} pr-3 ${NAV_SCALE.sub.row} ${NAV_SCALE.sub.text} ${NAV_SCALE.sub.weight} text-[var(--nav-heading)] hover:text-[var(--nav-heading-hover)] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-r-lg`}
                  >
                    <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    <span>Advanced ({advanced.length})</span>
                  </button>
                  {isOpen && (
                    <div className="space-y-0.5">
                      {advanced.map(renderLinkAt(padAt(itemDepth + 1)))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Horizontal variant of the grouped section nav (SPEC-139, promotion finding B2).
 * When the desktop sidebar is collapsed the vertical SectionNav disappears, so
 * converted pages would have no way to switch tabs. AppShell renders this strip
 * at the top of the page in that state. Same registry data, same `?tab=` links,
 * same visibleKeys filtering — the collapsed-nav answer lives ONCE here, not per
 * page. Advanced items are shown inline (no per-group toggle) since the strip is
 * a compact fallback.
 */
export function SectionNavBar({ groups, basePath, activeKey, visibleKeys, onNavigate }: SectionNavProps) {
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: visibleKeys ? g.items.filter((i) => visibleKeys.includes(i.key)) : g.items,
    }))
    .filter((g) => g.items.length > 0);

  if (visibleGroups.length === 0) return null;
  const firstVisibleKey = visibleGroups[0]?.items[0]?.key;

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto border-b border-[var(--border-subtle)] mb-6"
      style={{ scrollbarWidth: 'none' }}
      role="tablist"
      aria-label="Section tabs"
    >
      {visibleGroups.map((group, gi) => (
        <React.Fragment key={group.label || `g${gi}`}>
          {/* SPEC-459 — same heading token as the vertical strip and the sidebar. This
              variant is horizontal, so depth is carried by the divider between groups,
              not by a left pad. Measured 5.88:1 on --bg-base, the surface this bar
              renders on.
              `aria-hidden` (review finding 8): this container is a `role="tablist"`, whose
              only permitted children are tabs, so a named `<span>` here is illegal ARIA
              and assistive tech may drop it. The label is therefore VISUAL only, and the
              group name reaches screen readers through each tab's `aria-label` instead —
              same information, legal structure. Same treatment as the divider below. */}
          {group.label && (
            <span
              aria-hidden
              className={`pl-2 pr-0.5 ${NAV_SCALE.subLabel.text} text-[var(--nav-heading)] whitespace-nowrap flex-shrink-0`}
            >
              {group.label}
            </span>
          )}
          {group.items.map((item) => {
            const isActive = activeKey === item.key || (!activeKey && item.key === firstVisibleKey);
            return (
              <NavLink
                key={item.key}
                to={`${basePath}?tab=${item.key}`}
                onClick={onNavigate}
                title={item.tooltip}
                role="tab"
                aria-selected={isActive}
                // Carries the group the visual label states, since that label is
                // aria-hidden above. Undefined for an unlabelled group — the tab's own
                // text is then already the whole name.
                aria-label={group.label ? `${group.label} · ${item.label}` : undefined}
                // SPEC-535: the collapsed-sidebar tab bar shows the same sub-tabs, so it takes
                // tier 3 of the one scale (12px) — medium, because it is a bar of controls.
                className={`px-3 py-2.5 ${NAV_SCALE.sub.text} ${NAV_SCALE.sub.weightActive} whitespace-nowrap border-b-2 -mb-px transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${
                  isActive
                    ? 'border-amber-400 text-amber-400'
                    : 'border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)]'
                }`}
              >
                {item.label}
              </NavLink>
            );
          })}
          {group.label && gi < visibleGroups.length - 1 && (
            <span className="mx-1 h-4 w-px bg-[var(--border-subtle)] flex-shrink-0" aria-hidden />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
